import { useEffect, useMemo, useState } from "react";

import { useWhoAmI } from "../../graphql/customer/useWhoAmI";

import AuthContext, { CustomerInstigator, IAuthContext } from "./AuthContext";
import {
  AUTH_TOKEN_LOCAL_STORAGE_KEY,
  getToken,
  isExpired,
} from "./Token";

// unsupported browsers may not have access to this function, so we safely call it here to prevent crashing the whole app
const sessionId = crypto.randomUUID ? crypto.randomUUID() : "";

const AuthContextProvider: React.FC<{children: React.ReactNode}> = (props) => {
  const [token, setToken] = useState(() => getToken());
  const [instigator, setInstigator] = useState<CustomerInstigator>({
    accountID: "",
    accountNumber: "",
  });
  const [authenticated, setAuthenticated] = useState(false);

  const skip = !token.token.length || isExpired(token.expiry);
  const { hasUnauthorisedError, loading, whoAmI } = useWhoAmI(skip);

  useEffect(() => {
    localStorage.setItem(AUTH_TOKEN_LOCAL_STORAGE_KEY, token.token);

    if (
      hasUnauthorisedError ||
      isExpired(token.expiry) ||
      !token.token?.length ||
      !whoAmI
    ) {
      setAuthenticated(false);
    } else {
      setAuthenticated(true);
    }
  }, [token, hasUnauthorisedError, whoAmI]);

  useEffect(() => {
    if (whoAmI) {
      setInstigator((prev: CustomerInstigator) => ({
        ...prev,
        accountID: whoAmI.accountID,
        accountNumber: whoAmI.accountNumber,
      }));
    }
  }, [loading, whoAmI]);

  const clearToken = () => {
    setToken({token: "", expiry: 0});
  }

  const authContext: IAuthContext = useMemo(
    () => ({
      token: token.token,
      sessionId,
      instigator,
      authenticated,
      setAuthenticated,
      clearToken
    }),
    [token, instigator, authenticated, setAuthenticated]
  );

  if (!token || loading) {
    return null;
  }

  return (
    <AuthContext.Provider value={authContext}>
      {props.children}
    </AuthContext.Provider>
  );
};

export default AuthContextProvider;
